/**
 * The React-bound half of the resource contracts (#2167 PR-2, ADR-0035 D4).
 *
 * ADR-0035 D4 splits `src/types/schema.ts` on ONE axis: a declaration that
 * names no React and no TanStack type says WHAT a resource offers and belongs
 * to `@resource-kit/model`; a declaration that names one describes how it
 * RENDERS and belongs here. #2166 moved the first half. This file is the
 * remainder — the family that failed that test, moved verbatim:
 *
 *   `RendererContext` / `EntityFieldBase.render` name `ComponentType`,
 *   `ActionConfirmRender.render` and `ResourceDetailConfig.headerActions`
 *   name `ReactNode`, and `ResourceColumnMeta` exists only to be merged into
 *   TanStack's `ColumnMeta` at the bottom of this file.
 *
 * `@/types/schema` keeps re-exporting every name, so the ~70 modules importing
 * them are untouched (ADR-0035 D6; the shim retires in #2170).
 *
 * WHY NO `zod` IMPORT, given two members are zod schemas: ADR-0035
 * (`docs/adr/0035-resource-kit-independence.md`, D6's migration step 3 — the
 * one that stands this package up; D3 is the Provider seam and says nothing
 * about specifiers)
 * fixes this package's bare-specifier closed set at five names, and `zod` is
 * not one of them. `@resource-kit/model` already declares both shapes — a
 * field's own schema is `ModelField['schema']` exactly, and a confirm payload's
 * is that type narrowed back to its payload — so the type is reached THROUGH
 * the model rather than by widening the package's dependency surface. This is
 * #2167 invariant 7's device, which the root modules use for the same reason.
 */
import type { ComponentType, ReactNode } from 'react';
import type { RowData } from '@tanstack/react-table';
import type { ModelName, ResourceTabGateContext, WidgetName } from '@resource-kit/core';
import type {
  ActionConfirm,
  ActionFeedback,
  ActionMode,
  AfterAction,
  ColumnFilterMeta,
  DetailSurface,
  EmptyEncoding,
  EnumOption,
  FieldInvisibility,
  FieldModifier,
  FieldType,
  ModelField,
  StaticOptions,
} from '@resource-kit/model';

export interface RendererContext<T> {
  mode: 'list' | 'detail' | 'form';
  value: unknown;
  row: T;
  field: EntityField<T>;
}

/**
 * A zod schema for a custom-confirm payload, expressed WITHOUT naming `zod`.
 *
 * `NonNullable<ModelField['schema']>` is the model's own `ZodType<unknown>`,
 * which carries the whole surface a caller uses (`parse`, `safeParse`, the
 * refinement combinators); intersecting it with a narrowed `parse` is what
 * keeps `TPayload` bound, so `rowAction`'s author-side payload typing survives
 * the move. See the header for why the type is not imported from `zod`
 * directly.
 */
export type PayloadSchema<TPayload> = NonNullable<ModelField['schema']> & {
  parse: (data: unknown) => TPayload;
};

/**
 * Custom confirm-surface variant (#1416, rowActions v2 capability 1). Instead
 * of a fixed title/message, `render` returns the confirm UI (e.g. a Select +
 * note input) and collects a `payload` it hands to `submit`; the runner
 * validates that payload with `schema` (zod) and passes the PARSED value to
 * `execute(row, payload)`. `cancel` aborts with no execute. The framework is
 * agnostic to the payload shape — `schema` is the contract (use the
 * `rowAction` factory for author-side payload typing).
 */
export interface ActionConfirmRender<T, TPayload> {
  render: (row: T, submit: (payload: TPayload) => void, cancel: () => void) => ReactNode;
  /** zod schema the collected payload MUST satisfy before `onAction` runs. */
  schema: PayloadSchema<TPayload>;
}

/**
 * A single-row action surfaced in the ResourcePage row-actions dropdown
 * (#1287, extended to v2 in #1416). Consumed by `RowActionsCell`, which
 * mirrors the bulk-action pipeline for ONE row: permission gating, per-row
 * eligibility, confirm (static OR custom surface), result/error-branch
 * feedback, a shared per-row busy registry, and query invalidation after the
 * action resolves.
 *
 * `TResult` / `TPayload` type the settled execute value and the custom-confirm
 * payload; they default to `unknown` / `void` so pre-v2 definitions (a plain
 * `onAction: (row) => …`) are unchanged. Because `rowActions` is a
 * heterogeneous array (`ActionDefinition<T>[]` — erased generics), author-side
 * payload/result typing is provided by the `rowAction` factory below.
 */
export interface ActionDefinition<T, TResult = unknown, TPayload = unknown> {
  id: string;
  /** i18n key for the menu-item label. */
  label: string;
  /** Backend permission key gating the action; unset ⇒ ungated. */
  permission?: string;
  /**
   * Dispatch mode (#1416). `execute` (default) or `handoff`. See `ACTION_MODE`.
   */
  mode?: ActionMode;
  /**
   * Optional confirmation before the action runs — either static copy
   * (`ActionConfirm`) or a custom surface (`ActionConfirmRender`, #1416).
   * Ignored entirely in `handoff` mode.
   */
  confirm?: ActionConfirm | ActionConfirmRender<T, TPayload>;
  /** `danger` tints the item destructively (and the confirm as danger). */
  intent?: 'default' | 'danger';
  /** Per-row predicate; a row failing it omits the action from that row. */
  isEligible?: (row: T) => boolean;
  /**
   * The action to run for the row (sync or async); awaited by the runner,
   * and its settled value feeds `feedback` / `formatResult`. Receives the
   * zod-parsed `payload` when a custom-confirm surface collected one
   * (otherwise `undefined`). In `handoff` mode this is the page callback and
   * its return value is ignored.
   */
  onAction: (row: T, payload: TPayload) => unknown;
  /**
   * Result-branch feedback (#1416): maps the settled result to a closed-map
   * kind + i18n message. Unset ⇒ the historical single success toast.
   */
  feedback?: (result: TResult) => ActionFeedback;
  /**
   * Error-branch feedback (#1416): maps a thrown error to an i18n message key
   * (e.g. a 409 → `alreadyResolved`). Unset ⇒ the raw error message.
   */
  errorFeedback?: (err: unknown) => string;
  /**
   * Post-success panel behavior when this action runs from a detail-panel
   * footer (#1417): `close` leaves the detail on success and refreshes the
   * namespace without reading this record's detail again (the action may
   * have removed it); the detail is only marked stale, so a reopen reads it
   * (#2802). `refetch` (default) keeps it open.
   * Ignored by the row-actions dropdown (a table row has no panel to close)
   * and in `handoff` mode.
   */
  afterAction?: AfterAction;
  /** Maps the action result to the success-toast body (legacy single-toast;
   *  ignored when `feedback` is set). */
  formatResult?: (result: unknown) => string;
}

/**
 * Author-side typed factory for a row action (#1416). `rowActions` is a
 * heterogeneous `ActionDefinition<T>[]` (payload/result generics erased so
 * actions with different payload shapes coexist in one array). This factory
 * lets a callsite write a fully-typed `render`/`onAction`/`feedback` triple —
 * the compiler checks the payload flows from `submit` to `onAction` and the
 * result flows into `feedback` — then erases the generics for storage. The
 * single documented `as unknown as` cast is the standard erasure idiom for a
 * heterogeneous typed collection (the runtime stays sound: `schema` validates
 * the payload and the closures captured here own the concrete shapes).
 */
export function rowAction<T, TPayload = void, TResult = unknown>(
  def: ActionDefinition<T, TResult, TPayload>,
): ActionDefinition<T> {
  return def as unknown as ActionDefinition<T>;
}

/**
 * Shared field configuration (#1405). `EntityField<T>` below is a
 * discriminated union over this base: entity-backed columns get their
 * `key` compile-time checked against `T`, while synthetic / computed
 * columns must opt in explicitly with `virtual: true`.
 */
interface EntityFieldBase<T> {
  label: string;
  type: FieldType;

  // Optional: Override the type used for rendering (defaults to 'type')
  renderType?: FieldType;

  /**
   * The named widget this field renders and edits with — level 2 of the
   * resolution order (#1805). Level 1 is a v2 `ViewItem.widget`, which has no
   * legacy carrier (legacy holds ONE `fields[]` behind both the table and the
   * form, so a list-side override would silently take the form control too);
   * level 3 is the field type's default.
   *
   * A name nobody registered is an authoring mistake, not a degradation: it
   * throws in dev/test and logs in prod
   * (`packages/resource-kit-core/fail-loud.ts`).
   * Closed to the declared names, so a typo fails `tsc`.
   */
  widget?: WidgetName;

  /**
   * many2one: the model this field's value is an id INTO (#1804 batch C).
   *
   * Set it and the form slot stops showing a bare uuid: read-only renders the
   * target's name as a link, edit becomes a remote search select. Both halves
   * resolve the target through the relation registry, which is also why the
   * name is closed to the declared models — a typo here is a compile error,
   * not a blank cell.
   *
   * List columns are NOT affected: a table cell still shows the id.
   */
  relation?: ModelName;

  // Optional: Transform the value before rendering
  // Can be a string path (e.g. "items[].title | join(', ')") or a function
  transform?: string | ((value: unknown, row: T) => unknown);

  filterId?: string;
  // Override the QueryBuilder filter type (defaults to derived from 'type').
  filterType?: 'string' | 'number' | 'boolean' | 'enum' | 'date' | 'datetime' | 'id' | 'json';
  // Opt a column OUT of filter generation. Computed / display-only columns
  // whose declared `type` (or custom `render`) does not correspond to a
  // filterable backend field must set this to `false` so the generic filter
  // generator does not offer a wrong-typed operator set for them (e.g. a
  // numeric operator on a boolean, or a string filter on a JSON blob).
  // Defaults to filterable (undefined ⇒ true).
  filterable?: boolean;

  /**
   * Presentation only — never a security control (#1799). Hiding a field the
   * operator must not write is fake security: the request is one DOM edit
   * away. Use it for noise, not for protection.
   */
  invisible?: FieldInvisibility<T>;

  /**
   * Domain state: the field renders but the edit form shows it read-only.
   * Distinct from a permission (a user WITH the grant still cannot edit a
   * terminal record) and from `invisible` (the operator still sees it).
   * Whatever this expresses, the backend must reject the write too.
   */
  readonly?: FieldModifier<T>;

  /**
   * The record's contract: the server assigns this value and no surface may
   * offer it for writing (#1871) — unlike `readonly` directly above, which is
   * domain state and therefore has nothing to say on a create form.
   *
   * Materialised from `ModelField.serverAssigned` by `viewFieldOf`; a view
   * cannot produce it, because `ViewItem` has no such member.
   */
  serverAssigned?: boolean;

  /**
   * List only: the column is off in the table's default column set. The
   * column still exists — it stays filterable, exportable, and the operator
   * can switch it on from the column picker.
   */
  columnInvisible?: FieldModifier<T>;

  options?: StaticOptions;

  // Type-specific configuration
  enumValues?: EnumOption[];
  currencyCode?: string; // e.g., 'USD'

  /**
   * Framework-level copy affordance (#1291). When `true`, the default
   * cell renderer wraps the value in `IdCell` — a mono value with a
   * click-to-copy button (and the insecure-context clipboard fallback)
   * — regardless of the field's declared `type`. `id`-typed fields are
   * copyable implicitly (their renderer already routes through IdCell);
   * `copyable` extends that to non-id string fields operators
   * cross-reference against external systems (Onfleet / Shopify ids,
   * idempotency keys, hashes). Ignored when a custom `render` is set —
   * the callsite owns its own affordances then.
   */
  copyable?: boolean;

  // Custom rendering (optional overrides)
  render?: ComponentType<RendererContext<T>>;

  // Form configuration
  required?: boolean;
  placeholder?: string;
  description?: string;
  /**
   * Field-level form validation. Overrides the default schema derived from
   * `type`. Spelled as the model's own member rather than `ZodType<unknown>`
   * so the package names no `zod` — see the header.
   */
  schema?: ModelField['schema'];
  /**
   * Wire value for a CLEARED field, overriding the field type's repo default
   * (#1797). Set it wherever the endpoint's zod schema disagrees with the
   * type default — `''` for a `z.string().optional()` column that rejects
   * `null`, `null` for a `.nullable()` one.
   */
  encodeEmpty?: EmptyEncoding;
}

/**
 * A column definition (#1405 compile-time type-chain closure).
 *
 * Discriminated union on `virtual`:
 *   - entity branch (default): `key` MUST name a real property of the row
 *     type `T`. Because every resource `T` is (or converges on) a
 *     codegen-derived wire type, a backend field rename now fails `tsc`
 *     here instead of silently rendering `undefined`.
 *   - virtual branch (`virtual: true`): a synthetic / computed column with
 *     no backing property on `T` (e.g. an aggregate badge or a
 *     filter-only mirror). The free-form `key` is the accessor id used
 *     for column identity, `groups` references, and `filterId` fallback.
 */
export type EntityField<T> =
  | (EntityFieldBase<T> & {
      /** Accessor key — must exist on the row type `T`. */
      key: Extract<keyof T, string>;
      virtual?: false;
    })
  | (EntityFieldBase<T> & {
      /** Marks a synthetic column with no backing property on `T`. */
      virtual: true;
      /** Free-form accessor id for the synthetic column. */
      key: string;
    });

export interface EntityDefinition<T> {
  name: string;
  label: string;
  idField: keyof T;
  fields: EntityField<T>[];
}

// ─── Column meta contract ──────────────────────────────────
// Both legacy and native column builders stash schema metadata on each
// TanStack column's `meta` slot; FormView / ListView /
// DataTable read it back. Declaring the shape here (and augmenting
// TanStack's `ColumnMeta` below) replaces the untyped `col.meta`
// casts with checked access so a renamed meta field fails at compile
// time instead of silently reading `undefined`.

export interface ResourceColumnMeta<T> {
  /** Raw i18n key — translated at render time. */
  label?: string;
  filter?: ColumnFilterMeta;
  /** Hidden in the active mode (list vs detail). */
  hidden?: boolean;
  /** Back-reference to the source field definition. */
  field?: EntityField<T>;
}

/**
 * The augmentation travels WITH the interface it merges (#2167 PR-2).
 *
 * A `declare module` merges into the module that DECLARES it, and it takes
 * effect for every program that includes this file. `@/types/schema` used to
 * carry it and now only re-exports the names, which is why the block moved
 * here rather than staying behind: an augmentation left on the far side of a
 * re-export would merge whenever the shim happened to be in the program and
 * silently not merge when it was not.
 */
declare module '@tanstack/react-table' {
  // `TValue` is unused here but must be declared to merge with TanStack's
  // `ColumnMeta<TData, TValue>` signature.
  interface ColumnMeta<TData extends RowData, TValue> extends ResourceColumnMeta<TData> {}
}

/**
 * What the hosting detail surface hands its header actions.
 * `leaveDetail` is the same callback the panel receives as `onClose`: the
 * drawer closes, the standalone page returns to its list route. A header
 * command whose result removes the record (it can no longer be read)
 * calls it and refreshes through `invalidateAfterLeave`, so the surface
 * does not refetch a record that is gone (#2793, #2802). Undefined when
 * the host supplies no way to leave.
 */
export interface DetailHeaderContext {
  leaveDetail?: () => void;
}

export interface ResourceDetailConfig<T> {
  title?: (item: T) => string;
  subtitle?: (item: T) => string;
  /**
   * The verdict for every tab this resource's form declares `gated: true` on
   * (#1917 declares the gate, #1921 wires the first one), keyed by tab id.
   *
   * A CALLBACK, not a value: the declaration is authored at module load and
   * the verdict is an identity fact only known per render, so the host calls
   * this beside its other per-render props (`ResourceDetailPanel.tsx`) and
   * hands the result straight to `FormViewProps.tabGates`. Keeping it on the
   * bundle is what keeps it bundle-SCOPED — `FormView` fails loud on a key
   * that names no gated tab, so a globally-injected map would break the
   * other nineteen resources the moment one of them gained a gate.
   *
   * Absent ⇒ every gated tab of this resource is hidden, because a missing
   * wiring must never read as "allowed".
   */
  tabGates?: (ctx: ResourceTabGateContext) => Readonly<Record<string, boolean>>;
  /**
   * Key of an `enum`-typed field whose value drives a status badge
   * rendered next to the detail-header title (#1291). The badge reuses
   * the schema enum-badge treatment (color + localized label +
   * deterministic `enum-badge-<value>` testid). An empty/absent value
   * renders no badge; a field that is not an enum is ignored.
   */
  statusField?: keyof T | string;
  /**
   * Resource-specific actions rendered in the shared detail header. This is
   * where a record's COMMANDS belong: the header is on screen whichever tab
   * the rail is showing, so a command never costs a click to find (#1804).
   * Informational blocks belong on the rail instead — see `tabs`.
   */
  headerActions?: (item: T, ctx: DetailHeaderContext) => ReactNode;
  /**
   * Which detail surfaces this resource offers (#1392 D6). Governs the
   * list-page row-click behavior and the drawer header:
   *   - `drawer` — row click opens the Sheet drawer (no full-page link).
   *   - `page`   — row click NAVIGATES to `<listPath>/<id>` (no drawer).
   *   - `both`   — drawer PLUS an "open full page" link in the drawer header
   *                (the webhooks pattern, globalized). This is the default.
   * Absent ⇒ `both`.
   */
  surface?: DetailSurface;
  /**
   * Overrides the derived detail-page path used by `page`/`both` surfaces.
   * Receives the whole clicked ROW (not just its id) so the target can vary
   * by a row field — e.g. a live stocktake (`open`/`counted`) continues on
   * the count page while a terminal one opens the read-only review page
   * (#1418). Defaults to `<current list pathname>/<idField>` — correct for
   * every resource whose `/:id` route is a direct child of its list route.
   * Supply this only when that convention does not hold (a detail route
   * mounted under a different prefix, or a row-conditional destination).
   */
  pagePath?: (row: T) => string;
  /**
   * The list route a standalone `ResourceDetailPage` returns to when a
   * terminal footer action (`afterAction: 'close'`, e.g. delete) succeeds
   * (#1417). Declare it explicitly rather than letting the framework guess
   * the list path from the current URL shape — a nested route
   * (`/webhooks/failures/:id`) does not sit under `/<slug>`. Absent ⇒
   * the page falls back to stripping the known `:id` segment off the current
   * path.
   */
  listPath?: string;
  /**
   * Actions rendered in the detail panel's action region (#1417) — the
   * drawer's top row or the standalone page header. Reuses the rowActions v2
   * pipeline
   * (permission gate / confirm surface / result-branch feedback / the SHARED
   * per-row busy registry, so the same row's cell action and detail action
   * are mutually exclusive). An action's `afterAction` governs whether a
   * successful run also closes the panel. Absent ⇒ the region carries only
   * the legacy `onEdit`/`onDelete` affordances.
   */
  actions?: ActionDefinition<T>[];
}
